import React, { useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { format } from 'date-fns';
import globalUserPage from '../../../../stores/global-user-page';
import { observer } from 'mobx-react-lite';
import { UserPaymentItemWithPayment } from '../../../../api/types/students/payment-item/user-payment-item-with-payment';
import {
  PaymentMethodsDb,
  PaymentReferenceType,
  PaymentStatusType,
} from '../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';

export const UserPageStudentPaymentItems: React.FC = observer(() => {
  const paymentItems = useMemo(() => {
    return globalUserPage.studentDetails?.paymentItems ?? [];
  }, [globalUserPage.studentDetails?.paymentItems]);

  const formatDateTime = (date: string | undefined) => {
    if (!date) return '';
    return format(new Date(date), 'dd/MM/yyyy');
  };

  const formatAmount = (amount: number | undefined) => {
    if (amount === undefined || amount === null) return '';
    return `₪${amount.toLocaleString()}`;
  };

  const getPaymentMethodText = (method: PaymentMethodsDb | undefined) => {
    if (!method) return '';
    switch (method) {
      case PaymentMethodsDb.CreditCard:
        return 'כרטיס אשראי';
      case PaymentMethodsDb.Cash:
        return 'מזומן';
      case PaymentMethodsDb.Discount:
        return 'הנחה';
      case PaymentMethodsDb.BankTransfer:
        return 'העברה בנקאית';
      case PaymentMethodsDb.Scholarship:
        return 'מלגה';
      case PaymentMethodsDb.Check:
        return "צ'ק";
      default:
        return method;
    }
  };

  const getPaymentStatusText = (status: PaymentStatusType | undefined) => {
    if (!status) return '';
    switch (status) {
      case PaymentStatusType.SUCCESS:
        return 'הצליח';
      case PaymentStatusType.STANDING:
        return 'ממתין';
      case PaymentStatusType.CANCEL:
        return 'בוטל';
      default:
        return status;
    }
  };

  const getPaymentReferenceTypeText = (
    type: PaymentReferenceType | null | undefined,
  ) => {
    if (!type) return '-';
    switch (type) {
      case PaymentReferenceType.REVERSAL:
        return 'ביטול';
      case PaymentReferenceType.INSTALLMENT:
        return 'תשלום';
      default:
        return type;
    }
  };

  const paymentMethodTemplate = (rowData: UserPaymentItemWithPayment) => {
    return getPaymentMethodText(rowData.splitPaymentMethodType);
  };

  const paymentStatusTemplate = (rowData: UserPaymentItemWithPayment) => {
    const status = rowData.paymentStatus;
    let color = '#333';
    if (status === PaymentStatusType.SUCCESS) {
      color = '#22c55e';
    } else if (status === PaymentStatusType.CANCEL) {
      color = '#ef4444';
    } else if (status === PaymentStatusType.STANDING) {
      color = '#f59e0b';
    }
    return <span style={{ color }}>{getPaymentStatusText(status)}</span>;
  };

  const paymentReferenceTypeTemplate = (
    rowData: UserPaymentItemWithPayment,
  ) => {
    return getPaymentReferenceTypeText(rowData.paymentReferenceType);
  };

  return (
    <div style={{ width: '100%', padding: '20px' }}>
      <h2 style={{ marginBottom: '20px' }}>פירוט תשלומים</h2>
      <DataTable
        value={paymentItems}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        emptyMessage="לא נמצאו תשלומים"
        style={{ direction: 'rtl' }}
      >
        <Column
          field="id"
          header="מזהה פריט"
          sortable
          style={{ textAlign: 'right' }}
        />

        <Column
          field="paymentAmount"
          header="סכום תשלום"
          body={(rowData) => (
            <span style={{ direction: 'ltr', display: 'inline-block' }}>
              {formatAmount(rowData.paymentAmount)}
            </span>
          )}
          sortable
          style={{ textAlign: 'right' }}
        />

        <Column
          field="itemAmount"
          header="סכום פריט"
          body={(rowData) => (
            <span style={{ direction: 'ltr', display: 'inline-block' }}>
              {formatAmount(rowData.amount)}
            </span>
          )}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="paymentDate"
          header="תאריך תשלום"
          body={(rowData) => formatDateTime(rowData.paymentDate)}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="splitPaymentMethodType"
          header="אמצעי תשלום"
          body={paymentMethodTemplate}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="paymentStatus"
          header="סטטוס"
          body={paymentStatusTemplate}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="paymentReferenceType"
          header="סוג אסמכתא"
          body={paymentReferenceTypeTemplate}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="paymentCreatedAt"
          header="נוצר בתאריך"
          body={(rowData) => formatDateTime(rowData.paymentCreatedAt)}
          sortable
          style={{ textAlign: 'right' }}
        />
        <Column
          field="paymentCreatedByName"
          header="נוצר ע״י"
          sortable
          style={{ textAlign: 'right' }}
        />
      </DataTable>
    </div>
  );
});
